import { Center, Container, Text } from "@mantine/core";
import { Fragment, memo, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { type ISection } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { v4 as uuidv4 } from "uuid";

import NMEAAutomationModal from "../_Modals/NMEAAutomationModal/NMEAAutomationModal";
import PreTextCell from "../_PreText/PreTextSection/PreTextCell";
import { useCumulativeFeature } from "../hooks/useCumulativeFeature";
import { useShowContent } from "../hooks/useShowContent";
import LandscapeSection from "../LandscapeOrientation/LandscapeSection";
import PortraitSection from "../PortraitOrientation/PortraitSection/PortraitSection";
import SnapshotDisplay from "../SnapshotDisplay/SnapshotDisplay";
import { ReadonlyFieldset, StyledPreTextTable } from "./Logbook.styled";
import { LogbookRefsProvider } from "./LogbookRefsContext";
import { useIsDev } from "src/hooks/useIsDev";

interface Props {
  pageRef: React.RefObject<HTMLDivElement>;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  htmlId?: string;
}

const Logbook = (props: Props) => {
  const isDev = useIsDev();
  const { token } = useParams();
  const { sections } = useSelector(state => state.generatorSlice.lbentry);
  const { locked, you } = useSelector(state => state.entrySlice);
  const { isRestricted, shared, snapshotHeaders, snapshotEntry } = useSelector(
    state => state.generatorSlice,
  );
  const { pageSize, onPageSizeChange } = props;
  const isReadOnlyMode = useSelector(
    state => state.generatorSlice.lbentry.isReadonly,
  );
  const lbContainerRef = useRef(null);
  const { shouldHideElement } = useShowContent();
  const renderPreTextSection = (section: ISection) => {
    let pretextSection = null;
    if (section?.pretext) {
      pretextSection = section.pretext.map((pretext: any, index: number) => {
        return <PreTextCell key={index} pretext={pretext} />;
      });
    }
    return pretextSection;
  };
  const uid = uuidv4();
  useEffect(() => {
    const page = document.getElementById("page-container");
    const currentScroll = localStorage.getItem("scrollY");
    if (page && currentScroll) {
      page.scrollTo({ top: Number(currentScroll) });
    }
  }, [uid]);

  const displaySections = useCumulativeFeature(sections, {
    featureKey: "day_cumalative",
    destId: "day_cumalative_sum",
  });

  useEffect(() => {
    isDev && sections.length > 0 && console.log({ sections });
  }, [sections]);

  const renderSections = useMemo(
    () =>
      displaySections
        .filter(
          section =>
            !("shared" in section) || typeof section.shared !== "string",
        )
        .map((section: ISection, originalIdx: number) => ({
          section,
          originalIdx,
        }))
        .filter(({ section }) => !shouldHideElement(section))
        .map(({ section, originalIdx }) => (
          <Fragment key={`section-${originalIdx}`}>
            {!section.noGap && (
              <StyledPreTextTable mt={30}>
                {section?.pretext ? renderPreTextSection(section) : null}
              </StyledPreTextTable>
            )}
            {section.subSections ? (
              section.subSections.map((subSection: any, subIdx: number) => (
                <LandscapeSection
                  {...subSection}
                  dynamicRows={section.dynamicRows}
                  cloneRows={section.cloneRows}
                  sectionIndex={originalIdx}
                  subSectionIndex={subIdx}
                  collapseable={section.collapseable}
                  collapsed={section.collapsed}
                  key={`land-sect-${originalIdx}`}
                />
              ))
            ) : (
              <PortraitSection
                key={`section-body-${originalIdx}`}
                sectionIndex={originalIdx}
                {...section}
                {...props}
              />
            )}
          </Fragment>
        )),
    [shared, displaySections, shouldHideElement],
  );

  const renderSharedSections = useMemo(() => {
    if (shared && shared.length > 0) {
      const sharedSections = displaySections.filter(section => section.shared);
      const sharedIdxOffset = displaySections.length - sharedSections.length;
      return sharedSections.map((sharedSection, originalIdx) => {
        return sharedSection?.sections
          ?.map((section: ISection) => ({
            section,
            sectionIdx: originalIdx + sharedIdxOffset,
          }))
          .filter(({ section }) => !shouldHideElement(section))
          .map(({ section, sectionIdx }, localIdx) => {
            return (
              <Fragment key={`shared-section-${sectionIdx + localIdx}`}>
                <StyledPreTextTable mt={30}>
                  {section?.pretext ? renderPreTextSection(section) : null}
                </StyledPreTextTable>
                {section.subSections ? (
                  section.subSections.map((subSection: any, subIdx: number) => (
                    <LandscapeSection
                      {...subSection}
                      dynamicRows={section.dynamicRows}
                      cloneRows={section.cloneRows}
                      sectionIndex={sectionIdx}
                      subSectionIndex={subIdx}
                      collapseable={section.collapseable}
                      collapsed={section.collapsed}
                      key={`shared-land-sect-${sectionIdx + localIdx}`}
                      isShared={true}
                      sharedIndex={localIdx}
                    />
                  ))
                ) : (
                  <PortraitSection
                    key={`shared-section-body-${sectionIdx + localIdx}`}
                    isShared={true}
                    sharedIndex={localIdx}
                    sectionIndex={sectionIdx}
                    {...section}
                    {...props}
                  />
                )}
              </Fragment>
            );
          });
      });
    }
  }, [shared, displaySections, shouldHideElement]);

  return (
    <div
      ref={lbContainerRef}
      style={{
        position: "relative",
      }}
      id={props?.htmlId || undefined}>
      <ReadonlyFieldset disabled={(locked && !you) || isReadOnlyMode}>
        <LogbookRefsProvider>
          {sections?.length > 0 ? (
            <Fragment>
              <Fragment>{renderSections}</Fragment>
              <Fragment>{renderSharedSections}</Fragment>
              <SnapshotDisplay
                snapshotHeaders={snapshotHeaders}
                snapshotEntry={snapshotEntry}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
              />
            </Fragment>
          ) : isRestricted && !token ? (
            <Container w={"100%"}>
              <Center>
                <Text color="red">
                  You are not authorized to view this restricted Logbook!
                </Text>
              </Center>
            </Container>
          ) : null}
          <NMEAAutomationModal containerRef={lbContainerRef} />
        </LogbookRefsProvider>
      </ReadonlyFieldset>
    </div>
  );
};

export default memo(Logbook);

// SOME OLD CODE
// <List itemCount={sections.length} height="auto">
//
//   {({ index }: any) => (
//     <Fragment key={`section-${index}`}>
//       <StyledPreTextTable mt={30}>
//         {sections[index]?.pretext
//           ? renderPreTextSection(sections[index])
//           : null}
//       </StyledPreTextTable>
//       <Section sectionIndex={index} {...sections[index]} />
//     </Fragment>
//   )}
// </List>
